import {
  type Boiler,
  type FullBoilerRoom,
  type FullHeatingNetwork,
  type Pumps,
  type SludgeRemover,
} from "@eisox/api/core-api";
import {
  BoilerroomDefectType,
  BoilerroomObjectType,
  translateDefect,
  type TranslateDefect,
} from "@eisox/building-management-system";

import { getLocale } from "~/paraglide/runtime";
import type { BoilerRoomError } from "~/types/boiler-room.type";

const getMessage = (props: Omit<TranslateDefect, "language">) =>
  translateDefect({ ...(props as TranslateDefect), language: getLocale() });

const getPumpsErrors = (props: {
  objectType: BoilerroomObjectType;
  pumps: Pumps;
  boilerName?: string;
  heatingNetworkName?: string;
}): string[] => {
  const { objectType, pumps, boilerName, heatingNetworkName } = props;
  const errors: string[] = [];

  const getPumpsErrorMessage = (defectType: BoilerroomDefectType) =>
    getMessage({ objectType, defectType, boilerName, heatingNetworkName });

  if (pumps.defectP1) {
    errors.push(getPumpsErrorMessage(BoilerroomDefectType.DEFECT_P1));
  }

  if (pumps.defectP2) {
    errors.push(getPumpsErrorMessage(BoilerroomDefectType.DEFECT_P2));
  }

  if (pumps.missmatchDefectP1) {
    errors.push(getPumpsErrorMessage(BoilerroomDefectType.MISS_MATCH_DEFECT_P1));
  }

  if (pumps.missmatchDefectP2) {
    errors.push(getPumpsErrorMessage(BoilerroomDefectType.MISS_MATCH_DEFECT_P2));
  }

  return errors;
};

const getBoilerErrors = (boiler: Boiler): string[] => {
  const errors: string[] = [];

  const getBoilerErrorMessage = (
    props: Omit<Parameters<typeof getMessage>[0], "objectType" | "boilerName" | "heatingNetworkName">,
  ) => getMessage({ ...props, objectType: BoilerroomObjectType.BOILER, boilerName: boiler.name });

  if (boiler.ashPanDefect) {
    errors.push(getBoilerErrorMessage({ defectType: BoilerroomDefectType.ASH_PAN_DEFECT }));
  }

  if (boiler.defectBurner) {
    errors.push(getBoilerErrorMessage({ defectType: BoilerroomDefectType.DEFECT_BURNER }));
  }

  if (boiler.defect) {
    errors.push(getBoilerErrorMessage({ defectType: BoilerroomDefectType.DEFECT }));
  }

  if (boiler.missmatchDefect) {
    errors.push(getBoilerErrorMessage({ defectType: BoilerroomDefectType.MISS_MATCH_DEFECT }));
  }

  if (boiler.communicationDefect) {
    errors.push(
      getBoilerErrorMessage({
        defectType: BoilerroomDefectType.COMMUNICATION_DEFECT,
      }),
    );
  }

  if (boiler.thermostatDefect) {
    errors.push(getBoilerErrorMessage({ defectType: BoilerroomDefectType.THERMOSTAT_DEFECT }));
  }

  if (boiler.twoWaysValve?.defect) {
    errors.push(
      getMessage({
        objectType: BoilerroomObjectType.TWO_WAYS_VALVE,
        defectType: BoilerroomDefectType.DEFECT,
        boilerName: boiler.name,
      }),
    );
  }

  if (boiler.pumps) {
    errors.push(
      ...getPumpsErrors({ objectType: BoilerroomObjectType.BOILER_PUMP, pumps: boiler.pumps, boilerName: boiler.name }),
    );
  }

  return errors;
};

const getBoilersErrors = (boilers: Boiler[]): string[] => {
  const errors: string[] = [];

  boilers.forEach(boiler => {
    errors.push(...getBoilerErrors(boiler));
  });

  return errors;
};

const getHeatingNetworkErrors = (heatingNetwork: FullHeatingNetwork): string[] => {
  const errors: string[] = [];

  const getHeatingNetworkErrorMessage = (
    props: Omit<Parameters<typeof getMessage>[0], "objectType" | "boilerName" | "heatingNetworkName">,
  ) =>
    getMessage({ ...props, objectType: BoilerroomObjectType.HEATING_NETWORK, heatingNetworkName: heatingNetwork.name });

  if (heatingNetwork.thermostatDefect) {
    errors.push(getHeatingNetworkErrorMessage({ defectType: BoilerroomDefectType.THERMOSTAT_DEFECT }));
  }

  if (heatingNetwork.threeWaysValve?.defect) {
    errors.push(
      getMessage({
        objectType: BoilerroomObjectType.THREE_WAYS_VALVE,
        defectType: BoilerroomDefectType.DEFECT,
        heatingNetworkName: heatingNetwork.name,
      }),
    );
  }

  if (heatingNetwork.pumps) {
    errors.push(
      ...getPumpsErrors({
        objectType: BoilerroomObjectType.HEATING_NETWORK_PUMP,
        pumps: heatingNetwork.pumps,
        heatingNetworkName: heatingNetwork.name,
      }),
    );
  }

  if (heatingNetwork.recyclePumpsECS) {
    errors.push(
      ...getPumpsErrors({
        objectType: BoilerroomObjectType.RECYCLE_PUMPS_ECS,
        pumps: heatingNetwork.recyclePumpsECS,
        heatingNetworkName: heatingNetwork.name,
      }),
    );
  }

  return errors;
};

const getHeatingNetworksErrors = (heatingNetworks: FullHeatingNetwork[]): string[] => {
  const errors: string[] = [];

  heatingNetworks.forEach(heatingNetwork => {
    errors.push(...getHeatingNetworkErrors(heatingNetwork));
  });

  return errors;
};

const getSludgeRemoverErrors = (sludgeRemover: SludgeRemover): string[] => {
  const errors: string[] = [];

  if (sludgeRemover.defect) {
    errors.push(
      getMessage({ objectType: BoilerroomObjectType.SLUDGE_REMOVER, defectType: BoilerroomDefectType.DEFECT }),
    );
  }

  if (sludgeRemover.pumps) {
    errors.push(
      ...getPumpsErrors({ objectType: BoilerroomObjectType.SLUDGE_REMOVER_PUMP, pumps: sludgeRemover.pumps }),
    );
  }

  return errors;
};

const getBoilerRoomErrorMessage = (props: Omit<Parameters<typeof getMessage>[0], "objectType">) =>
  getMessage({ ...props, objectType: BoilerroomObjectType.BOILER_ROOM });

const getBoilerRoomErrors = (boilerRoom: FullBoilerRoom): BoilerRoomError[] => {
  const errors: BoilerRoomError[] = [];

  const boilerRoomId = boilerRoom.id;
  const boilerRoomName = boilerRoom.name;

  if (boilerRoom.gazDefect)
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.GAZ_DEFECT }),
    });

  if (boilerRoom.waterDefect) {
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.WATER_DEFECT }),
    });
  }

  if (boilerRoom.waterKeeperDefect) {
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.WATER_KEEPER_DEFECT }),
    });
  }

  if (boilerRoom.waterTreatmentDefect) {
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.WATER_TREATMENT_DEFECT }),
    });
  }

  if (boilerRoom.gazDetectionDefect) {
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.GAZ_DETECTION_DEFECT }),
    });
  }

  if (boilerRoom.saltTankDefect) {
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.SALT_TANK_DEFECT }),
    });
  }

  if (boilerRoom.externalTempDefect) {
    errors.push({
      boilerRoomId,
      boilerRoomName,
      message: getBoilerRoomErrorMessage({ defectType: BoilerroomDefectType.EXTERNAL_TEMP_DEFECT }),
    });
  }

  if (boilerRoom.recyclePumps) {
    const errorMessages = getPumpsErrors({
      objectType: BoilerroomObjectType.BOILER_ROOM_RECYCLE_PUMP,
      pumps: boilerRoom.recyclePumps,
    });
    errors.push(...errorMessages.map(m => ({ boilerRoomId, boilerRoomName, message: m })));
  }

  if (boilerRoom.sludgeRemover) {
    const errorMessages = getSludgeRemoverErrors(boilerRoom.sludgeRemover);
    errors.push(...errorMessages.map(m => ({ boilerRoomId, boilerRoomName, message: m })));
  }

  if (boilerRoom.boilers) {
    const errorMessages = getBoilersErrors(boilerRoom.boilers);
    errors.push(...errorMessages.map(m => ({ boilerRoomId, boilerRoomName, message: m })));
  }

  if (boilerRoom.heatingNetworks) {
    const errorMessages = getHeatingNetworksErrors(boilerRoom.heatingNetworks);
    errors.push(...errorMessages.map(m => ({ boilerRoomId, boilerRoomName, message: m })));
  }

  return errors;
};

const getErrors = (boilerRooms: FullBoilerRoom[]): BoilerRoomError[] => {
  const errors: BoilerRoomError[] = [];

  boilerRooms.forEach(boilerRoom => {
    errors.push(...getBoilerRoomErrors(boilerRoom));
  });

  return errors;
};

export { getErrors };
export type { BoilerRoomError };
